// Pages/ExpertServicesLearnerPage.js
import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Avatar,
  Tag,
  Button,
  Rate,
  Input,
  Select,
  Spin,
  Empty,
  Pagination,
  Modal,
  Form,
  InputNumber,
  message,
  Divider,
  Space,
  Tooltip,
  Badge,
  Alert,
  Radio
} from "antd";
import {
  SearchOutlined,
  UserOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  StarOutlined,
  BookOutlined,
  TeamOutlined,
  ShoppingOutlined,
  CheckCircleOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom"; 
import { useExpertServiceLearner } from "PlatForms/SelfLMS/Hooks/ExpertService/useExpertServiceLearner";
import SoftBox from "Common/hooks/StyleFeatures/SoftBox";
import SoftButton from "Common/hooks/StyleFeatures/SoftButton";
import SoftTypography from "Common/hooks/StyleFeatures/SoftTypography";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const ExpertServicesLearnerPage = () => {
  const navigate = useNavigate();
  const {
    services,
    loading,
    error,
    pagination,
    searchTerm,
    fetchServices,
    getServiceDetails,
    purchaseService,
    updateSearchTerm
  } = useExpertServiceLearner();

  const [selectedService, setSelectedService] = useState(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [purchaseModalVisible, setPurchaseModalVisible] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedPricingId, setSelectedPricingId] = useState(null);

  // Handle page change
  const handlePageChange = (page) => {
    fetchServices(page);
  };

  // Handle search
  const handleSearch = (value) => {
    updateSearchTerm(value);
    fetchServices(1);
  };

  // Show service details
  const showServiceDetails = async (service) => {
    try {
      const details = await getServiceDetails(service.id);
      setSelectedService(details);
      setDetailsModalVisible(true);
    } catch (error) {
      console.error("Error fetching service details:", error);
    }
  };

  // Show purchase modal
  const showPurchaseModal = (service) => {
    setSelectedService(service);
    // Set the default pricing as selected initially
    const defaultPricing = service.pricings?.find(p => p.isDefault);
    setSelectedPricingId(defaultPricing ? defaultPricing.id : (service.pricings?.[0]?.id || null));
    setPurchaseModalVisible(true);
  };

  // Handle purchase
  const handlePurchase = async () => {
    if (!selectedPricingId) {
      message.error("Please select a pricing option");
      return;
    }
    
    setPurchasing(true);
    try {
      await purchaseService(selectedPricingId);
      setPurchaseModalVisible(false);
      setSelectedService(null);
      setSelectedPricingId(null);
      // Refresh services list
      fetchServices(pagination.pageNumber);
    } catch (error) {
      console.error("Error purchasing service:", error);
    } finally {
      setPurchasing(false);
    }
  };

  // Format duration
  const formatDuration = (minutes) => {
    if (!minutes) return "0 min";
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  // Format price
  const formatPrice = (price, currency = "USD") => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  // Render service card
  const renderServiceCard = (service) => {
    return (
      <Col xs={24} sm={12} lg={8} xl={6} key={service.id}>
        <Card
          hoverable
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
          cover={
            <div style={{ 
              height: 100,
              background: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.5)), url(${service.expert?.profileImage || '/default-expert.jpg'})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              alignItems: 'flex-end',
              padding: 12
            }}>
              <div style={{ color: 'white' }}>
                <Title level={5} style={{ color: 'white', margin: 0, fontSize: '16px' }}>
                  {service.title}
                </Title>
              </div>
            </div>
          }
          actions={[
            <Button 
              key="view-details" 
              type="text" 
              onClick={() => navigate(`/learner/expert-services/${service.id}`)}
            >
              View Details
            </Button>,
            <Button 
              key="purchase" 
              type="primary" 
              icon={<ShoppingOutlined />}
              onClick={() => showPurchaseModal(service)}
            >
              Purchase
            </Button>
          ]}
        >
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Expert Info */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <Avatar 
                src={service.expert?.profileImage} 
                icon={<UserOutlined />}
                size="small" 
                style={{ marginRight: 8 }}
              />
              <div>
                <Text strong>{service.expert?.firstName} {service.expert?.lastName}</Text>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {service.expert?.expertCode} • {service.expert?.yearsOfExperience || 0} years exp.
                </div>
              </div>
            </div>
            
            {/* Description */}
            <Paragraph 
              ellipsis={{ rows: 2 }} 
              style={{ marginBottom: 8, flex: 1, fontSize: '14px' }}
            >
              {service.description}
            </Paragraph>
            
            {/* Pricing Options - Show all options */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
                Pricing Options:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {service.pricings?.slice(0, 3).map((pricing, index) => (
                  <div key={pricing.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text style={{ fontSize: '12px' }}>
                      {formatDuration(pricing.durationMinutes)}
                    </Text>
                    <Text strong style={{ fontSize: '12px', color: '#1890ff' }}>
                      {formatPrice(pricing.price, pricing.currency)}
                      {pricing.isDefault 
                
                     }
                    </Text>
                  </div>
                ))}
                {service.pricings?.length > 3 && (
                  <Text type="secondary" style={{ fontSize: '11px', textAlign: 'center' }}>
                    +{service.pricings.length - 3} more options available
                  </Text>
                )}
              </div>
            </div>
            
            {/* Status */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Badge status={service.isActive ? 'success' : 'default'} text={service.isActive ? 'Available' : 'Unavailable'} />
            </div>
          </div>
        </Card>
      </Col>
    );
  };

  if (error) {
    return (
      <SoftBox py={3}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={3}>Error Loading Services</Title>
          <Text>{error}</Text>
          <Button type="primary" onClick={() => fetchServices()} style={{ marginTop: 16 }}>
            Try Again
          </Button>
        </div>
      </SoftBox>
    );
  }

  return (
    <SoftBox py={3}>
      <SoftBox mb={3}>
        <SoftTypography variant="h4" fontWeight="medium" mb={1}>
          Expert Services
        </SoftTypography>
        <SoftTypography variant="body2" color="text">
          Purchase one-on-one sessions with industry experts
        </SoftTypography>
      </SoftBox>

    

      {/* Services Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : services.length === 0 ? (
        <Empty 
          description="No expert services found" 
          style={{ padding: '50px' }}
        />
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {services.map(renderServiceCard)}
          </Row>
          
          {/* Pagination */}
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <Pagination
              current={pagination.pageNumber}
              total={pagination.totalCount}
              pageSize={pagination.pageSize}
              onChange={handlePageChange}
              showSizeChanger={false}
              showQuickJumper
              showTotal={(total, range) => 
                `${range[0]}-${range[1]} of ${total} services`
              }
            />
          </div>
        </>
      )}

      {/* Service Details Modal */}
      <Modal
        title={selectedService?.title}
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailsModalVisible(false)}>
            Close
          </Button>,
          <Button 
            key="purchase" 
            type="primary" 
            icon={<ShoppingOutlined />}
            onClick={() => {
              showPurchaseModal(selectedService);
              setDetailsModalVisible(false);
            }}
          >
            Purchase
          </Button>
        ]}
        width={800}
      >
        {selectedService && (
          <div>
            {/* Expert Info */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <Avatar 
                src={selectedService.expert?.profileImage} 
                icon={<UserOutlined />}
                size="large" 
                style={{ marginRight: 16 }}
              />
              <div style={{ flex: 1 }}>
                <Title level={4} style={{ margin: 0 }}>
                  {selectedService.expert?.firstName} {selectedService.expert?.lastName}
                </Title>
                <div style={{ color: '#666' }}>
                  {selectedService.expert?.expertCode} • {selectedService.expert?.yearsOfExperience || 0} years experience
                </div>
                <div style={{ color: '#666' }}>
                  <EnvironmentOutlined style={{ marginRight: 4 }} />
                  {selectedService.expert?.city}, {selectedService.expert?.country}
                </div>
              </div>
            </div>

            <Divider />

            {/* Description */}
            <div style={{ marginBottom: 16 }}>
              <Title level={5}>About This Service</Title>
              <Paragraph>{selectedService.description}</Paragraph>
            </div>

            {/* Pricing Options */}
            <div>
              <Title level={5}>Pricing Options</Title>
              <div>
                {selectedService.pricings?.map((pricing, index) => (
                  <Card 
                    key={pricing.id || index} 
                    size="small" 
                    style={{ marginBottom: 8, border: pricing.isDefault ? '1px solid #1890ff' : '' }}
                 
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                          {formatPrice(pricing.price, pricing.currency)}
                        </Text>
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                          for {formatDuration(pricing.durationMinutes)}
                        </Text>
                        {pricing.notes && (
                          <div style={{ marginTop: 4 }}>
                            <Text type="secondary">{pricing.notes}</Text>
                          </div>
                        )}
                      </div>
                      <Button 
                        type="primary" 
                        size="small"
                        icon={<ShoppingOutlined />}
                        onClick={() => {
                          showPurchaseModal(selectedService);
                          setDetailsModalVisible(false);
                        }}
                      >
                        Purchase
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Purchase Confirmation Modal with Radio Buttons */}
      <Modal
        title="Select Pricing Option"
        open={purchaseModalVisible}
        onCancel={() => {
          setPurchaseModalVisible(false);
          setSelectedService(null);
          setSelectedPricingId(null);
        }}
        footer={[
          <Button 
            key="cancel" 
            onClick={() => {
              setPurchaseModalVisible(false);
              setSelectedService(null);
              setSelectedPricingId(null);
            }}
          >
            Cancel
          </Button>,
          <Button 
            key="confirm" 
            type="primary" 
            loading={purchasing}
            onClick={handlePurchase}
          >
            Purchase
          </Button>
        ]}
        width={600}
      >
        {selectedService && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Service: </Text>
              <Text>{selectedService.title}</Text>
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <Text strong>Expert: </Text>
              <Text>{selectedService.expert?.firstName} {selectedService.expert?.lastName}</Text>
            </div>
            
            <Divider />
            
            <div style={{ marginBottom: 16 }}>
              <Text strong>Select a pricing option:</Text>
              <Radio.Group 
                value={selectedPricingId} 
                onChange={(e) => setSelectedPricingId(e.target.value)}
                style={{ width: '100%', marginTop: 8 }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {selectedService.pricings?.map((pricing) => (
                    <Card 
                      key={pricing.id} 
                      size="small" 
                      style={{ 
                        border: selectedPricingId === pricing.id ? '1px solid #1890ff' : '',
                        cursor: 'pointer'
                      }}
                      onClick={() => setSelectedPricingId(pricing.id)}
                    >
                      <Radio value={pricing.id} style={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <div>
                            <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                              {formatPrice(pricing.price, pricing.currency)}
                            </Text>
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                              for {formatDuration(pricing.durationMinutes)}
                            </Text>
                           
                          </div>
                        </div>
                        {pricing.notes && (
                          <div style={{ marginTop: 4, marginLeft: 24 }}>
                            <Text type="secondary">{pricing.notes}</Text>
                          </div>
                        )}
                      </Radio>
                    </Card>
                  ))}
                </Space>
              </Radio.Group>
            </div>

            <Divider />

            <Alert
              message="Purchase Confirmation"
              description="Once you confirm, the purchase will be processed and you'll receive access to this expert service. You can find your purchased services in your dashboard."
              type="info"
              showIcon
              icon={<CheckCircleOutlined />}
            />
          </div>
        )}
      </Modal>
    </SoftBox>
  );
};

export default ExpertServicesLearnerPage;